import axios from 'axios';

// Sempre usa URL relativa — o proxy do Vite (dev) e o servidor (prod) resolvem
const API_BASE_URL = '/api';

export interface UsuarioPayload {
  idUsuario: number;
  usuario: string;
  nomeUsuario: string;
  domCargo: string;
  domAdministrador: boolean;
  permissoes: string[];
}

export interface LoginResponse {
  success: boolean;
  usuario?: UsuarioPayload;
  message?: string;
}

const USER_KEY = 'tresco_user';

/**
 * Realiza login do usuário
 * O token JWT agora é retornado como cookie HttpOnly pelo backend
 * @param rememberMe Se true, sessão persiste por 7 dias. Se false, expira ao fechar o browser.
 */
export const login = async (usuario: string, senha: string, rememberMe: boolean = true): Promise<LoginResponse> => {
  try {
    const response = await axios.post<LoginResponse>(
      `${API_BASE_URL}/auth/login`,
      { usuario, senha, rememberMe },
      { withCredentials: true }
    );

    if (response.data.success && response.data.usuario) {
      // Salvar apenas dados do usuário (sem token) para exibição
      localStorage.setItem(USER_KEY, JSON.stringify(response.data.usuario));
    }

    return response.data;
  } catch (error: unknown) {
    console.error('[Auth] Erro no login:', error);
    if (axios.isAxiosError(error) && error.response?.data?.message) {
      return {
        success: false,
        message: error.response.data.message
      };
    }
    return {
      success: false,
      message: 'Erro ao conectar com o servidor'
    };
  }
};

/**
 * Realiza logout do usuário
 * Revoga o refresh token no servidor e limpa cookies
 */
export const logout = async (): Promise<void> => {
  try {
    await axios.post(`${API_BASE_URL}/auth/logout`, {}, { withCredentials: true });
  } catch (error) {
    console.error('[Auth] Erro ao fazer logout no servidor:', error);
  }
  localStorage.removeItem(USER_KEY);
};

/**
 * Verifica se o usuário está autenticado (verificação local rápida)
 * A verificação real é feita via validateToken (que usa o cookie)
 */
export const isAuthenticated = (): boolean => {
  return !!localStorage.getItem(USER_KEY);
};

/**
 * Retorna os dados do usuário logado (cache local)
 */
export const getUser = (): UsuarioPayload | null => {
  const userStr = localStorage.getItem(USER_KEY);
  if (!userStr) return null;
  
  try {
    return JSON.parse(userStr) as UsuarioPayload;
  } catch {
    return null;
  }
};

/**
 * Valida a sessão com o servidor usando o cookie HttpOnly
 * Se o access_token expirou, tenta refresh automaticamente
 */
export const validateToken = async (): Promise<boolean> => {
  try {
    // Tenta acessar /me com o cookie access_token
    const response = await axios.get<LoginResponse>(
      `${API_BASE_URL}/auth/me`,
      { withCredentials: true }
    );

    if (response.data.success && response.data.usuario) {
      localStorage.setItem(USER_KEY, JSON.stringify(response.data.usuario));
      return true;
    }

    await logout();
    return false;
  } catch (error) {
    // Se 401/403, tentar refresh
    if (axios.isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403)) {
      return await tryRefresh();
    }
    await logout();
    return false;
  }
};

/**
 * Tenta renovar a sessão usando o refresh_token (cookie HttpOnly)
 */
export const tryRefresh = async (): Promise<boolean> => {
  try {
    const response = await axios.post<LoginResponse>(
      `${API_BASE_URL}/auth/refresh`,
      {},
      { withCredentials: true }
    );

    if (response.data.success && response.data.usuario) {
      localStorage.setItem(USER_KEY, JSON.stringify(response.data.usuario));
      return true;
    }

    await logout();
    return false;
  } catch {
    await logout();
    return false;
  }
};
